import { useState, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import API from '../../utils/api';
import PageHeader from '../../components/common/PageHeader';
import EmptyState from '../../components/common/EmptyState';
import Badge from '../../components/common/Badge';
import { HiOutlineLogin, HiOutlineCheck, HiOutlineClock } from 'react-icons/hi';

const STATUS_STYLES = {
  present:  { variant: 'success', label: 'উপস্থিত', icon: '✓' },
  absent:   { variant: 'danger',  label: 'অনুপস্থিত', icon: '✕' },
  half_day: { variant: 'warning', label: 'আধা দিন',  icon: '◐' },
};

const WorkerAttendance = () => {
  const [sites, setSites] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSite, setSelectedSite] = useState('');
  const [busy, setBusy] = useState(false);
  const { t } = useLanguage();

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [sitesRes, attendanceRes] = await Promise.all([
        API.get('/worker/attendance/sites'),
        API.get('/worker/attendance/my'),
      ]);
      setSites(sitesRes.data.sites);
      setAttendance(attendanceRes.data.attendance);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckIn = async () => {
    if (!selectedSite) { alert('একটি সাইট নির্বাচন করুন'); return; }
    setBusy(true);
    try {
      await API.post('/worker/attendance/check-in', { site_id: selectedSite });
      fetchData();
    } catch (error) {
      alert(error.response?.data?.message || 'ত্রুটি হয়েছে');
    } finally {
      setBusy(false);
    }
  };

  const today = new Date().toISOString().split('T')[0];
  const todayAttendance = attendance.find((a) => a.date === today);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-brand-200 border-t-brand-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <PageHeader
        title={t('attendance')}
        subtitle={todayAttendance ? `আজ: ${todayAttendance.site?.name || '-'}` : 'আজ এখনো চেক-ইন করা হয়নি'}
      />

      {/* Action card */}
      <div className={`card ${
        todayAttendance
          ? 'bg-gradient-to-br from-brand-50 to-emerald-50 dark:from-brand-900/20 dark:to-emerald-900/20 border-brand-200 dark:border-brand-800'
          : 'bg-gradient-to-br from-ink-50 to-white dark:from-ink-900/30 dark:to-ink-800'
      }`}>
        {todayAttendance ? (
          // ALREADY CHECKED IN — show confirmation only (no checkout)
          <div className="flex items-center gap-4 p-3">
            <div className="w-14 h-14 rounded-2xl bg-brand-600 text-white flex items-center justify-center text-2xl shadow-pop">
              <HiOutlineCheck />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-brand-700 dark:text-brand-300">আজ হাজিরা দেওয়া হয়েছে</p>
              <p className="text-lg font-bold text-ink-900 dark:text-white font-mono mt-0.5">
                সময়: {todayAttendance.check_in}
              </p>
              {todayAttendance.site && (
                <p className="text-xs text-ink-500 dark:text-ink-400 mt-0.5 truncate">
                  সাইট: {todayAttendance.site.name}
                </p>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-gradient-brand flex items-center justify-center text-white text-xl shadow-pop">
                <HiOutlineClock />
              </div>
              <div>
                <h2 className="text-base font-semibold text-ink-900 dark:text-white">{t('checkIn')}</h2>
                <p className="text-xs text-ink-500 dark:text-ink-400">আজ {today}</p>
              </div>
            </div>
            <select value={selectedSite} onChange={(e) => setSelectedSite(e.target.value)} className="input-field">
              <option value="">— সাইট নির্বাচন করুন —</option>
              {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <button onClick={handleCheckIn} data-welcome="check-in-btn" disabled={busy || !selectedSite} className="btn-success btn-lg w-full">
              <HiOutlineLogin className="w-5 h-5" />
              {busy ? t('loading') : t('checkIn')}
            </button>
          </div>
        )}
      </div>

      {/* History */}
      <div className="card p-0 overflow-hidden">
        <div className="px-5 pt-5 pb-3">
          <h2 className="text-base font-semibold text-ink-900 dark:text-white flex items-center gap-2">
            <span className="w-1.5 h-6 rounded-full bg-accent-500" />
            {t('attendanceHistory')}
          </h2>
        </div>
        {attendance.length === 0 ? (
          <EmptyState icon="📅" title="কোনো হাজিরা নেই" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-ink-50 dark:bg-ink-900/30 border-y border-ink-100 dark:border-ink-700">
                <tr>
                  <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-ink-500 dark:text-ink-400">তারিখ</th>
                  <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-ink-500 dark:text-ink-400">সাইট</th>
                  <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-ink-500 dark:text-ink-400">সময়</th>
                  <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-ink-500 dark:text-ink-400">{t('status')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100 dark:divide-ink-700">
                {attendance.map((a) => {
                  const style = STATUS_STYLES[a.status] || STATUS_STYLES.present;
                  return (
                    <tr key={a.id} className="hover:bg-ink-50 dark:hover:bg-ink-900/30 transition-colors">
                      <td className="px-5 py-3 text-sm font-medium text-ink-900 dark:text-white whitespace-nowrap">{a.date}</td>
                      <td className="px-5 py-3 text-sm text-ink-600 dark:text-ink-400">{a.site?.name}</td>
                      <td className="px-5 py-3 text-sm font-mono text-ink-700 dark:text-ink-300">{a.check_in || '-'}</td>
                      <td className="px-5 py-3">
                        <Badge variant={style.variant}>{style.icon} {style.label}</Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkerAttendance;
